// src/hooks/useFilters.js
import { useMemo, useState, useCallback } from "react";
import { daysSince } from "../lib/utils";
import { parseSearchQuery, matchesSearchTerms } from "../lib/searchParser";

export function useFilters(requests) {
  // filters (Requests tab)
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    () => localStorage.getItem("statusFilter") || "all"
  );
  const [agencyFilter, setAgencyFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [sortBy, setSortBy] = useState("agency"); // agency, newest, old

  // Database tab filters
  const [dbFilterText, setDbFilterText] = useState("");
  const [dbStatus, setDbStatus] = useState("all");
  const [dbAgency, setDbAgency] = useState("");
  const [dbLocation, setDbLocation] = useState("");
  const [dbDateFrom, setDbDateFrom] = useState("");
  const [dbDateTo, setDbDateTo] = useState("");
  const [dbOverdueOnly, setDbOverdueOnly] = useState(false);

  // ---------------- SAVED SEARCH FUNCTIONS ----------------
  // Get current filters for saving
  const getCurrentFilters = useCallback(() => ({
    // Requests tab filters
    filterText,
    statusFilter,
    agencyFilter,
    locationFilter,
    sortBy,
    // Database tab filters
    dbFilterText,
    dbStatus,
    dbAgency,
    dbLocation,
    dbDateFrom,
    dbDateTo,
    dbOverdueOnly,
  }), [
    filterText, statusFilter, agencyFilter, locationFilter, sortBy,
    dbFilterText, dbStatus, dbAgency, dbLocation, dbDateFrom, dbDateTo, dbOverdueOnly
  ]);

  // Load filters from a saved search
  const loadFilters = useCallback((savedFilters) => {
    // Load Requests tab filters
    if (savedFilters.filterText !== undefined) setFilterText(savedFilters.filterText);
    if (savedFilters.statusFilter !== undefined) setStatusFilter(savedFilters.statusFilter);
    if (savedFilters.agencyFilter !== undefined) setAgencyFilter(savedFilters.agencyFilter);
    if (savedFilters.locationFilter !== undefined) setLocationFilter(savedFilters.locationFilter);
    if (savedFilters.sortBy !== undefined) setSortBy(savedFilters.sortBy);
    
    // Load Database tab filters
    if (savedFilters.dbFilterText !== undefined) setDbFilterText(savedFilters.dbFilterText);
    if (savedFilters.dbStatus !== undefined) setDbStatus(savedFilters.dbStatus);
    if (savedFilters.dbAgency !== undefined) setDbAgency(savedFilters.dbAgency);
    if (savedFilters.dbLocation !== undefined) setDbLocation(savedFilters.dbLocation);
    if (savedFilters.dbDateFrom !== undefined) setDbDateFrom(savedFilters.dbDateFrom);
    if (savedFilters.dbDateTo !== undefined) setDbDateTo(savedFilters.dbDateTo);
    if (savedFilters.dbOverdueOnly !== undefined) setDbOverdueOnly(savedFilters.dbOverdueOnly);
  }, []);

  // ---------------- HELPER FUNCTIONS ----------------
  function isVerifiedRow(row) {
    const s = String(row.how_to_request ?? row.notes ?? "").toLowerCase();
    return s.includes("from verified contact data:");
  }

  function readAgency(row) {
    return String(row.agency || "").trim();
  }

  function readLocation(row) {
    return String(row.location || "").trim();
  }

  // ---------------- REQUESTS TAB FILTERS ----------------
  const base = useMemo(() => {
    if (!requests?.length) return [];
    if (statusFilter === "verified")
      return requests.filter((r) => r.status === "not_requested" && isVerifiedRow(r));
    if (statusFilter === "unverified")
      return requests.filter((r) => r.status === "not_requested" && !isVerifiedRow(r));
    return requests.filter((r) => r.status === "not_requested");
  }, [requests, statusFilter]);

  const textFiltered = useMemo(() => {
    if (!filterText) return base;
    
    // Parse the search query for advanced operators
    const searchTerms = parseSearchQuery(filterText);
    
    return base.filter((r) => {
      // Combine all searchable fields into a single text for matching
      const searchableText = [
        r.article_url,
        r.agency,
        r.location,
        r.name_suspect,
        r.incident_date,
        r.notes,
        r.status,
        r.how_to_request,
        r.email_for_request,
        r.draft_subject,
        r.draft_email
      ]
        .map((x) => String(x || ""))
        .join(" ");
        
      return matchesSearchTerms(searchableText, searchTerms);
    });
  }, [base, filterText]);

  const agencyOptions = useMemo(() => {
    const map = new Map();
    for (const r of textFiltered) {
      const label = readAgency(r);
      if (!label) continue;
      const key = label.toLowerCase();
      if (!map.has(key)) map.set(key, label);
    }
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  }, [textFiltered]);

  const locationOptions = useMemo(() => {
    const map = new Map();
    for (const r of textFiltered) {
      const label = readLocation(r);
      if (!label) continue;
      const key = label.toLowerCase();
      if (!map.has(key)) map.set(key, label);
    }
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  }, [textFiltered]);

  const finalRows = useMemo(() => {
    let rows = textFiltered.slice();
    if (agencyFilter) {
      const key = agencyFilter.toLowerCase();
      rows = rows.filter((r) => readAgency(r).toLowerCase() === key);
    }
    if (locationFilter) {
      const key = locationFilter.toLowerCase();
      rows = rows.filter((r) => readLocation(r).toLowerCase() === key);
    }
    // Apply sorting based on sortBy option
    rows.sort((a, b) => {
      if (sortBy === "newest") {
        // Sort by created_at descending (newest first)
        const aDate = a.created_at || "";
        const bDate = b.created_at || "";
        if (aDate !== bDate) return bDate.localeCompare(aDate);
        // Secondary sort by agency
        const aA = (a.agency || "").toLowerCase();
        const bA = (b.agency || "").toLowerCase();
        return aA.localeCompare(bA);
      } else if (sortBy === "oldest") {
        // Sort by created_at ascending (oldest first)
        const aDate = a.created_at || "";
        const bDate = b.created_at || "";
        if (aDate !== bDate) return aDate.localeCompare(bDate);
        // Secondary sort by agency
        const aA = (a.agency || "").toLowerCase();
        const bA = (b.agency || "").toLowerCase();
        return aA.localeCompare(bA);
      } else {
        // Default: sort by agency, then location, then date
        const aA = (a.agency || "").toLowerCase();
        const bA = (b.agency || "").toLowerCase();
        if (aA !== bA) return aA.localeCompare(bA);
        const aL = (a.location || "").toLowerCase();
        const bL = (b.location || "").toLowerCase();
        if (aL !== bL) return aL.localeCompare(bL);
        return (a.created_at || "").localeCompare(b.created_at || "");
      }
    });
    return rows;
  }, [textFiltered, agencyFilter, locationFilter, sortBy]);

  const totalNotRequested = requests.filter((r) => r.status === "not_requested").length;
  const verifiedCount = requests.filter((r) => r.status === "not_requested" && isVerifiedRow(r)).length;
  const unverifiedCount = requests.filter((r) => r.status === "not_requested" && !isVerifiedRow(r)).length;
  const shownCount = finalRows.length;
  const anyFilterActive =
    !!filterText || statusFilter !== "all" || !!agencyFilter || !!locationFilter;

  // ---------------- DATABASE TAB FILTERS ----------------
  const dbStatusOptions = useMemo(() => {
    const set = new Set(requests.map(r => r.status).filter(Boolean));
    const arr = Array.from(set).sort((a,b)=>a.localeCompare(b));
    return ["all", ...arr];
  }, [requests]);

  const dbAgencyOptions = useMemo(() => {
    const set = new Set(requests.map(r => readAgency(r)).filter(Boolean));
    return ["", ...Array.from(set).sort((a,b)=>a.localeCompare(b))];
  }, [requests]);

  const dbLocationOptions = useMemo(() => {
    const set = new Set(requests.map(r => readLocation(r)).filter(Boolean));
    return ["", ...Array.from(set).sort((a,b)=>a.localeCompare(b))];
  }, [requests]);

  const dbRows = useMemo(() => {
    let rows = requests.slice();
    if (dbStatus !== "all") rows = rows.filter(r => r.status === dbStatus);
    if (dbAgency) rows = rows.filter(r => readAgency(r) === dbAgency);
    if (dbLocation) rows = rows.filter(r => readLocation(r) === dbLocation);
    
    if (dbDateFrom) {
      rows = rows.filter(r => {
        if (!r.created_at) return false;
        return r.created_at >= dbDateFrom;
      });
    }
    if (dbDateTo) {
      rows = rows.filter(r => {
        if (!r.created_at) return false;
        return r.created_at <= dbDateTo + "T23:59:59";
      });
    }

    if (dbOverdueOnly) {
      rows = rows.filter(r => {
        if (r.status !== "requested") return false;
        const days = daysSince(r.date_requested);
        return days !== null && days > 30;
      });
    }

    if (dbFilterText) {
      // Parse the search query for advanced operators
      const searchTerms = parseSearchQuery(dbFilterText);
      
      rows = rows.filter(r => {
        // Combine all searchable fields for database search
        const searchableText = [
          r.article_url, r.agency, r.location, r.name_suspect,
          r.incident_date, r.notes, r.status, r.how_to_request,
          r.email_for_request, r.draft_subject, r.draft_email, r.fee
        ]
          .map(x => String(x || ""))
          .join(" ");
          
        return matchesSearchTerms(searchableText, searchTerms);
      });
    }
    rows.sort((a,b)=>String(b.created_at||"").localeCompare(String(a.created_at||"")));
    return rows;
  }, [requests, dbStatus, dbAgency, dbLocation, dbFilterText, dbDateFrom, dbDateTo, dbOverdueOnly]);

  return {
    // Requests tab filters
    filterText,
    setFilterText,
    statusFilter,
    setStatusFilter,
    agencyFilter,
    setAgencyFilter,
    locationFilter,
    setLocationFilter,
    sortBy,
    setSortBy,
    agencyOptions,
    locationOptions,
    finalRows,
    totalNotRequested,
    verifiedCount,
    unverifiedCount,
    shownCount,
    anyFilterActive,
    isVerifiedRow,
    
    // Database tab filters
    dbFilterText,
    setDbFilterText,
    dbStatus,
    setDbStatus,
    dbStatusOptions,
    dbAgency,
    setDbAgency,
    dbAgencyOptions,
    dbLocation,
    setDbLocation,
    dbLocationOptions,
    dbDateFrom,
    setDbDateFrom,
    dbDateTo,
    setDbDateTo,
    dbOverdueOnly,
    setDbOverdueOnly,
    dbRows,

    // Saved search functions
    getCurrentFilters,
    loadFilters,
  };
}
